import pako from "pako";
import { extractFromCompoundString } from "./preuseErrorDecode";

export function base64ToBytes(base64) {
    if (base64 == null) {
      throw new Error("base64ToBytes: missing input");
    }
  
    // Query strings often turn '+' into space; and many producers emit base64url.
    let normalized = String(base64).trim().replace(/ /g, "+").replace(/-/g, "+").replace(/_/g, "/");
  
    // Strip characters that can show up from copying/wrapping.
    normalized = normalized.replace(/[\r\n\t]/g, "");
  
    // Add required padding.
    const pad = normalized.length % 4;
    if (pad === 2) normalized += "==";
    else if (pad === 3) normalized += "=";
    else if (pad === 1) {
      throw new Error("base64ToBytes: invalid base64 length");
    }
  
    // Basic validation before calling atob (gives clearer errors).
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) {
      throw new Error("base64ToBytes: invalid base64 characters");
    }
  
    const binary = atob(normalized);
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
  }

  export function base64ToUtf8(base64) {
    const bytes = base64ToBytes(base64);
    return new TextDecoder("utf-8").decode(bytes);
  }
  
  export function decodePreUse(buffer) {
    if (!(buffer instanceof Uint8Array)) {
      throw new Error("decodePreUse: expected Uint8Array");
    }
  
    // Respect byteOffset/byteLength (Uint8Array may be a view into a larger buffer).
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    let offset = 0;
  
    // Header: foreignkey (8) + count (2)
    if (view.byteLength < 10) {
      throw new Error("decodePreUse: buffer too small");
    }
  
    const foreignkey = Number(view.getBigUint64(offset));
    offset += 8;
  
    const count = view.getUint16(offset);
    offset += 2;
  
    // Each record is 20 bytes: HHBBIHQ
    const recordSize = 20;
    const expectedMin = 10 + count * recordSize;
    if (view.byteLength < expectedMin) {
      throw new Error(
        `decodePreUse: truncated buffer (need ${expectedMin} bytes, got ${view.byteLength})`
      );
    }
  
    const result = [];
  
    for (let i = 0; i < count; i++) {
      const testid = view.getUint16(offset); offset += 2;
      const name_id = view.getUint16(offset); offset += 2;
      const passed = view.getUint8(offset) !== 0; offset += 1;
      const critical = view.getUint8(offset) !== 0; offset += 1;
      const data = view.getUint32(offset); offset += 4;
      const data_id = view.getUint16(offset); offset += 2;
      let time = Number(view.getBigUint64(offset)); offset += 8;
  
      // If upstream sends unix seconds, normalize to ms for JS Date().
      if (time > 0 && time < 1e12) time *= 1000;
  
      result.push({
        foreignkey,
        testid,
        name_id,
        passed,
        critical,
        data: [{ data, id: data_id }],
        time
      });
    }
  
    return result;
  }

  function parseValue(raw) {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "number") return raw;
    if (typeof raw !== "string") return raw;

    const trimmed = raw.trim();
    if (!trimmed) return trimmed;

    // Value can be stringified JSON array like "[0]" or "[1,2,3]".
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        return JSON.parse(trimmed);
      } catch (_) {
        return trimmed;
      }
    }

    return trimmed;
  }

  function parsePassFail(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["1", "true", "pass", "passed", "ok", "success"].includes(normalized)) return true;
      if (["0", "false", "fail", "failed", "error"].includes(normalized)) return false;
    }
    return Boolean(value);
  }

  /**
   * Optional device / build versions on JSON payload root (copied onto each row for the report header).
   * @param {Record<string, unknown>} jsonObject
   */
  function payloadVersionFields(jsonObject) {
    const j = jsonObject;
    const versions =
      j?.content && typeof j.content === "object" && !Array.isArray(j.content)
        ? j.content.versions || {}
        : {};
    const pick = (a, b) => {
      const v = j[a] ?? j[b] ?? versions[a] ?? versions[b];
      if (v == null || v === "") return undefined;
      return String(v);
    };
    /** @type {Record<string, string>} */
    const out = {};
    const systemVersion = pick("systemVersion", "system_version");
    const osVersion = pick("osVersion", "os_version");
    const firmwareVersion = pick("firmwareVersion", "firmware_version");
    const softwareVersion = pick("softwareVersion", "software_version");
    const uiVersion = pick("uiVersion", "ui_version");
    const backendVersion = pick("backendVersion", "backend_version");
    const firmwareMasterVersion = pick("firmwareMasterVersion", "firmware_master_version");
    const firmwareSlaveVersion = pick("firmwareSlaveVersion", "firmware_slave_version");
    const firmwarePowerBoardVersion = pick(
      "firmwarePowerBoardVersion",
      "firmware_power_board_version"
    );
    if (systemVersion !== undefined) out.systemVersion = systemVersion;
    if (osVersion !== undefined) out.osVersion = osVersion;
    if (firmwareVersion !== undefined) out.firmwareVersion = firmwareVersion;
    if (softwareVersion !== undefined) out.softwareVersion = softwareVersion;
    if (uiVersion !== undefined) out.uiVersion = uiVersion;
    if (backendVersion !== undefined) out.backendVersion = backendVersion;
    if (firmwareMasterVersion !== undefined) out.firmwareMasterVersion = firmwareMasterVersion;
    if (firmwareSlaveVersion !== undefined) out.firmwareSlaveVersion = firmwareSlaveVersion;
    if (firmwarePowerBoardVersion !== undefined) {
      out.firmwarePowerBoardVersion = firmwarePowerBoardVersion;
    }
    return out;
  }

  export function decodeJsonPayload(jsonObject) {
    if (!jsonObject || typeof jsonObject !== "object") {
      throw new Error("decodeJsonPayload: invalid JSON object");
    }

    const rootContent = jsonObject.content;
    let rows = Array.isArray(rootContent)
      ? rootContent
      : Array.isArray(rootContent?.preuse_tests)
        ? rootContent.preuse_tests
        : null;


    // Supported shapes:
    // 1) { content: [...rows], machineUDI }
    // 2) { content: { preuse_tests: [...rows], versions }, machineUDI }
    // 3) [...rows]
    if (Array.isArray(jsonObject)) {
      rows = jsonObject;
    } else if (Array.isArray(jsonObject.content)) {
      rows = jsonObject.content;
    } else if (jsonObject.content && Array.isArray(jsonObject.content.preuse_tests)) {
      rows = jsonObject.content.preuse_tests;
    }

    if (!Array.isArray(rows)) {
      throw new Error("decodeJsonPayload: missing content array");
    }

    const versionMeta = payloadVersionFields(jsonObject);

    return rows.map((row, index) => {
      if (!Array.isArray(row) || row.length < 5) {
        throw new Error(`decodeJsonPayload: invalid row at index ${index}`);
      }

      // Row format: [testid, epochMs time, pass/fail, errorcode, str data array]
      const [testid, time, passFail, errorCode, rawValue] = row;
      const parsedValue = parseValue(rawValue);
      const firstValue = Array.isArray(parsedValue)
        ? Number(parsedValue[0] ?? 0)
        : Number(parsedValue);

      const parsedErr = parseErrorCodeField(Number(testid), errorCode);

      return {
        machineUDI: jsonObject.machineUDI || "",
        ...versionMeta,
        testid: Number(testid),
        // Keep shape compatible with existing table.
        name_id: Number(testid),
        passed: parsePassFail(passFail),
        ...(parsedErr.compound_error_code ? { compound_error_code: parsedErr.compound_error_code } : {}),
        errorcode: parsedErr.errorcode,
        // Backward compatibility with old UI field.
        critical: (Number(errorCode) || 0) !== 0,
        data: [{ data: Number.isFinite(firstValue) ? firstValue : 0, id: 0 }],
        raw_data: parsedValue,
        time: Number(time),
      };
    });
  }

  /**
   * Same compound layout as REST `decodePreUseErrorCode` when `errorCode` string is TT+DATA+ERROR or TT+ERROR.
   */
  function parseErrorCodeField(testid, errorCodeRaw) {
    if (typeof errorCodeRaw === "string") {
      const s = errorCodeRaw.trim();
      try {
        const p = extractFromCompoundString(testid, s);
        return {
          errorcode: p.maskedErrorCode >>> 0,
          compound_error_code: s,
        };
      } catch (_) {
        const hx = /^0x/i.test(s) ? s.slice(2) : s;
        const n = Number.parseInt(hx, 16);
        if (Number.isFinite(n)) return { errorcode: n >>> 0 };
      }
    }
    const n = Number(errorCodeRaw);
    return { errorcode: Number.isFinite(n) ? n >>> 0 : 0 };
  }

  export function decodePayload(encoded) {
    const bytes = base64ToBytes(encoded);
    let text = "";

    // Java producer: Base64 URL-safe encoded GZIP bytes.
    // Try gzip first when magic header is present: 1F 8B.
    if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) 
    {
      try {
        text = pako.ungzip(bytes, { to: "string" }).trim();
      } catch (e) {
        throw new Error(`decodePayload: failed to ungzip payload (${e?.message || "unknown error"})`);
      }
    } else {
      // Uncompressed base64(JSON) fallback.
      text = base64ToUtf8(encoded).trim();
    }

    // JSON payload path: { content: [...], machineUDI: "..." }
    if (text.startsWith("{") || text.startsWith("[")) {
      const parsed = JSON.parse(text);
      return decodeJsonPayload(parsed);
    }

    // Backward compatibility: old packed binary payload.
    return decodePreUse(bytes);
  }